import React from 'react';
import { motion } from 'framer-motion';
import { Globe } from 'lucide-react';
import { SectionHeader } from './shared/SectionHeader';
import { formatCurrencyCompact, formatStat } from '../utils/formatters';

const SOURCE_COLORS = {
  'google': '#4285F4', 'meta': '#1877F2', 'instagram': '#E4405F',
  'facebook': '#1877F2', 'bing': '#008373', 'direct': '#10B981',
  'email': '#F59E0B', 'referral': '#EC4899', 'tiktok': '#000000',
  'criteo': '#F48120', 'pinterest': '#E60023',
};

function getSourceColor(source) {
  const lower = (source || '').toLowerCase();
  for (const [key, color] of Object.entries(SOURCE_COLORS)) {
    if (lower.includes(key)) return color;
  }
  return '#6B7280';
}

export function TrafficOverview({ trafficSourcesAnalytics, isMarketer }) {
  if (isMarketer) return null;

  const data = trafficSourcesAnalytics?.tableData || [];
  const totals = trafficSourcesAnalytics?.totals || {};
  const top20 = data.slice(0, 20);

  if (top20.length === 0) return null;

  const maxSessions = Math.max(...top20.map(d => d.sessions || 0), 1);

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-50px' }}
      transition={{ duration: 0.5 }}
      className="mb-6"
    >
      <SectionHeader
        title="Traffic Sources"
        icon={Globe}
        subtitle={`${formatStat(totals.sessions || 0)} total sessions`}
      />
      <div className="glass-card rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="modern-table w-full">
            <thead>
              <tr>
                <th className="text-left pl-5">Source / Medium</th>
                <th className="text-right">Sessions</th>
                <th className="text-left pl-4" style={{ width: '20%' }}></th>
                <th className="text-right">Transactions</th>
                <th className="text-right">Conv. Rate</th>
                <th className="text-right pr-5">Revenue</th>
              </tr>
            </thead>
            <tbody>
              {top20.map((row, i) => {
                const barPct = maxSessions > 0 ? (row.sessions / maxSessions) * 100 : 0;
                const sourceColor = getSourceColor(row.source);
                return (
                  <tr key={i}>
                    <td className="pl-5">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full shrink-0" style={{ background: sourceColor }} />
                        <span className="text-xs font-medium text-gray-800">{row.source}</span>
                        <span className="text-[10px] text-gray-400">/ {row.medium}</span>
                      </div>
                    </td>
                    <td className="text-right text-xs font-semibold text-gray-800">{formatStat(row.sessions)}</td>
                    <td className="pl-4">
                      <div className="h-[3px] rounded-full bg-gray-100 overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all duration-500"
                          style={{ width: `${barPct}%`, background: sourceColor }}
                        />
                      </div>
                    </td>
                    <td className="text-right text-xs text-gray-600">{formatStat(row.transactions || 0)}</td>
                    <td className="text-right text-xs text-gray-600">{(row.conversionRate || 0).toFixed(2)}%</td>
                    <td className="text-right pr-5 text-xs font-semibold text-gray-800">{formatCurrencyCompact(row.revenue)}</td>
                  </tr>
                );
              })}
              {/* Totals row */}
              <tr className="bg-gray-50/50 font-bold">
                <td className="pl-5 text-xs font-bold text-gray-800">Total</td>
                <td className="text-right text-xs font-bold text-gray-800">{formatStat(totals.sessions)}</td>
                <td></td>
                <td className="text-right text-xs font-bold text-gray-800">{formatStat(totals.transactions)}</td>
                <td className="text-right text-xs font-bold text-gray-800">{(totals.conversionRate || 0).toFixed(2)}%</td>
                <td className="text-right pr-5 text-xs font-bold text-gray-800">{formatCurrencyCompact(totals.revenue)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </motion.div>
  );
}
